/**
 * The two lists the register's toolbar offers that it cannot know by itself: the
 * tag vocabulary, and the clients.
 *
 * They live beside the toolbar rather than inside it because a component that
 * fetches is a component nobody can read at a glance, and because the toolbar is
 * deliberately stateless about everything else - what it is showing comes from
 * the address bar, and these two lists are the exception that has to come from
 * the server. The staff list is NOT here: `useStaff` already owns it, shares one
 * request across every picker on the page, and marks the people who have left.
 *
 * Neither hook throws and neither reports an error. A filter whose options did
 * not arrive is a menu that says it has nothing to offer - which is what the
 * operator sees anyway - while an error banner over a register that loaded
 * perfectly well would be the screen shouting about the wrong thing. The
 * register itself is the page; these are the controls on top of it.
 */
import { useEffect, useState } from "react";
import { getClientOptions, getProjectTags } from "../../../lib/projects";

/**
 * The tags any live project carries.
 *
 * Fetched per mount rather than cached for the session: the vocabulary changes
 * whenever somebody edits a project, and this list is small (one narrow column
 * over a few hundred rows on the server). A stale menu here is worse than a
 * repeated request - it offers a tag that no longer exists, and the operator
 * reads the empty result as a broken filter.
 */
export function useProjectTags(): string[] {
  const [tags, setTags] = useState<string[]>([]);

  useEffect(() => {
    let live = true;
    getProjectTags()
      .then((r) => {
        if (live) setTags(r.rows);
      })
      .catch(() => {});
    return () => {
      live = false;
    };
  }, []);

  return tags;
}

/**
 * How many clients the filter can offer, and the ceiling the endpoint allows.
 *
 * Worth saying out loud, because it is a limit and not a round number: with more
 * clients than this the filter offers the first hundred BY NAME and the rest are
 * unreachable from this control. The day that bites, the fix is a search inside
 * the picker that queries the server (what `ClientPicker` does in the forms),
 * not a bigger number here - the clients list endpoint carries contacts, counts
 * and a payment rollup per row, and asking it for everything on every visit to
 * the register would make the register slower the longer the CRM lives.
 */
const CLIENT_LIMIT = 100;

export interface ClientOption {
  id: string;
  name: string;
}

/**
 * The clients, by name, for a one-of-N filter.
 *
 * Ordered by the server (`orderBy: { name: "asc" }`), so the menu reads like a
 * directory rather than like whatever came back first.
 */
export function useClientOptions(): ClientOption[] {
  const [clients, setClients] = useState<ClientOption[]>([]);

  useEffect(() => {
    let live = true;
    getClientOptions(undefined, CLIENT_LIMIT)
      .then((r) => {
        if (live) setClients(r.rows);
      })
      .catch(() => {});
    return () => {
      live = false;
    };
  }, []);

  return clients;
}
